import { useState } from "react";
import { useTranslation } from "react-i18next";
import { hrApi, useAttendance, useCorrections, useHrLive, usePunches, useUnknownPins } from "../../hooks/hr";
import { useLocale } from "../../hooks/useLocale";
import { formatTime } from "../../lib/format";
import type { AttendanceRow, HrLevel, LivePerson } from "../../lib/typesHr";
import { Empty, SectionHeader } from "../ui/Badges";
import { Modal } from "../ui/Modal";
import { dateOnly, EmployeeSelect, Field, Kpi, mins, StatusPill, Table, todayIso, useAction, useEmpName } from "./shared";

/** Real-time board computed from raw punches: who is in, who is late, who has not come yet. */
export function LiveBoard() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const live = useHrLive();
  const name = useEmpName();
  const people: LivePerson[] = (live.data?.people ?? []).filter((p) => p.state !== "off");
  const s = live.data?.summary;
  return (
    <section className="card space-y-3 p-5" aria-label={t("hr.live.title")}>
      <SectionHeader icon="🟢" title={t("hr.live.title")} subtitle={t("hr.live.subtitle")} />
      {s && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Kpi label={t("hr.live.inside")} value={s.inside} tone="green" />
          <Kpi label={t("hr.live.late")} value={s.late} tone="orange" />
          <Kpi label={t("hr.live.expected")} value={s.expected} />
          <Kpi label={t("hr.live.left")} value={s.left} tone="purple" />
          <Kpi label={t("hr.live.leave")} value={s.leave} tone="purple" />
        </div>
      )}
      {people.length === 0 ? <Empty>{t("hr.live.empty")}</Empty> : (
        <Table head={[t("hr.employee"), t("hr.shift"), t("hr.attendance.in"), t("hr.attendance.out"), t("hr.status.label")]}>
          {people.map((p) => (
            <tr key={p.employee_id}>
              <td className="font-black">{name(p)} <span className="num font-mono text-[10px] text-gray-400">{p.employee_no}</span></td>
              <td className="num">{p.shift_name ? `${p.shift_name} ${p.shift_start}–${p.shift_end}` : "—"}</td>
              <td className="num">{p.first_punch ? formatTime(p.first_punch, locale) : "—"}</td>
              <td className="num">{p.punches > 1 && p.last_punch ? formatTime(p.last_punch, locale) : "—"}</td>
              <td><StatusPill status={p.state} ns="live.state" />{p.state === "late" && <span className="num ms-1 text-[10px] font-bold text-amber-700">+{p.lateMinutes}′</span>}</td>
            </tr>
          ))}
        </Table>
      )}
    </section>
  );
}

function PunchesDialog({ row, date, onClose }: { row: AttendanceRow | null; date: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const punches = usePunches(row?.employee_id ?? null, date);
  const name = useEmpName();
  return (
    <Modal open={!!row} title={`${t("hr.attendance.punches")} — ${name(row)} — ${dateOnly(date, locale)}`} onClose={onClose}>
      {(punches.data ?? []).length === 0 ? <Empty>{t("hr.attendance.noPunches")}</Empty> : (
        <Table head={[t("hr.time"), t("hr.attendance.direction"), t("hr.attendance.source"), t("hr.attendance.device"), ""]}>
          {punches.data!.map((p) => (
            <tr key={p.id}>
              <td className="num font-bold">{formatTime(p.punched_at, locale)}</td>
              <td>{t(`hr.direction.${p.direction}`, { defaultValue: p.direction })}</td>
              <td>{t(`hr.source.${p.source}`, { defaultValue: p.source })}{p.verify_mode ? ` · ${t(`hr.verify.${p.verify_mode}`, { defaultValue: p.verify_mode })}` : ""}</td>
              <td>{p.device_name ?? "—"}</td>
              <td>{p.reason ? <span className="text-[10px] text-gray-500">{p.reason}</span> : null}</td>
            </tr>
          ))}
        </Table>
      )}
    </Modal>
  );
}

export function CorrectionDialog({ open, onClose, forOthers = false }: { open: boolean; onClose: () => void; forOthers?: boolean }) {
  const { t } = useTranslation();
  const { act, busy } = useAction();
  const [employeeId, setEmployeeId] = useState("");
  const [date, setDate] = useState(todayIso());
  const [inT, setInT] = useState("09:00");
  const [outT, setOutT] = useState("17:00");
  const [reason, setReason] = useState("");
  const iso = (time: string) => new Date(`${date}T${time}:00`).toISOString();
  async function submit() {
    const r = await act(() => hrApi.post("/corrections", { employeeId: forOthers ? employeeId : undefined, workDate: date, kind: "full_day", requestedIn: iso(inT), requestedOut: iso(outT), reason: reason.trim() }), "hr.attendance.correctionSent");
    if (r) onClose();
  }
  return (
    <Modal open={open} title={t("hr.attendance.correction")} onClose={onClose} footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={busy || reason.trim().length < 3 || (forOthers && !employeeId)} onClick={() => void submit()}>{t("hr.leave.submit")}</button></>}>
      <p className="text-xs text-gray-500">{t("hr.attendance.correctionHelp")}</p>
      {forOthers && <Field label={t("hr.employee")}><EmployeeSelect value={employeeId} onChange={setEmployeeId} /></Field>}
      <Field label={t("hr.date")}><input type="date" className="field num" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("hr.attendance.in")}><input type="time" className="field num" value={inT} onChange={(e) => setInT(e.target.value)} /></Field>
        <Field label={t("hr.attendance.out")}><input type="time" className="field num" value={outT} onChange={(e) => setOutT(e.target.value)} /></Field>
      </div>
      <Field label={t("hr.reason")}><textarea className="field min-h-16" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
    </Modal>
  );
}

export function AttendancePanel({ level }: { level: HrLevel }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const name = useEmpName();
  const [date, setDate] = useState(todayIso());
  const att = useAttendance(date);
  const unknown = useUnknownPins(level >= 2);
  const corrections = useCorrections("pending");
  const { act, busy } = useAction();
  const [drill, setDrill] = useState<AttendanceRow | null>(null);
  const [corr, setCorr] = useState(false);

  const t_ = (iso: string | null) => (iso ? formatTime(iso, locale) : "—");
  return (
    <div className="space-y-5">
      {date === todayIso() && <LiveBoard />}
      <section className="card space-y-3 p-5" aria-label={t("hr.attendance.title")}>
        <SectionHeader icon="🕘" title={t("hr.attendance.title")} subtitle={t("hr.attendance.subtitle")}
          right={<div className="flex flex-wrap items-center gap-2">
            <input type="date" className="field num !w-auto" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} aria-label={t("hr.date")} />
            {level >= 2 && <button type="button" className="btn-ghost" disabled={busy} onClick={() => void act(() => hrApi.post("/attendance/recompute", { from: date, to: date }), "hr.attendance.recomputed")}>{t("hr.attendance.recompute")}</button>}
            <button type="button" className="btn-primary" onClick={() => setCorr(true)}>{t("hr.attendance.correction")}</button>
          </div>} />
        {level >= 2 && (unknown.data ?? []).length > 0 && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-3 text-xs font-bold text-amber-900">
            ⚠️ {t("hr.attendance.unknownPins", { count: unknown.data!.length })}: {unknown.data!.map((u) => <span key={u.pin} className="num me-2 rounded-lg bg-white px-1.5 py-0.5 font-mono">{u.pin} ×{u.punches}</span>)}
          </div>
        )}
        <Table head={[t("hr.employee"), t("hr.shift"), t("hr.status.label"), t("hr.attendance.in"), t("hr.attendance.out"), t("hr.attendance.worked"), t("hr.attendance.late"), t("hr.attendance.overtime"), ""]}
          empty={(att.data?.rows ?? []).length === 0 ? <Empty>{t("hr.attendance.none")}</Empty> : undefined}>
          {(att.data?.rows ?? []).map((r) => (
            <tr key={r.employee_id}>
              <td className="font-black">{name(r)} <span className="num font-mono text-[10px] text-gray-400">{r.employee_no}</span></td>
              <td>{r.shift_name ?? "—"}</td>
              <td>{r.status ? <StatusPill status={r.status} /> : <span className="text-[10px] font-semibold text-gray-400">{t("hr.attendance.notComputed")}</span>}</td>
              <td className="num">{t_(r.first_in)}</td>
              <td className="num">{t_(r.last_out)}</td>
              <td className="num">{r.worked_minutes ? mins(r.worked_minutes) : "—"}</td>
              <td className={`num ${r.late_minutes ? "font-black text-amber-700" : ""}`}>{r.late_minutes ? `${r.late_minutes}′` : "—"}</td>
              <td className="num">
                {r.overtime_minutes ? (
                  <span className="inline-flex items-center gap-1">
                    {mins(r.overtime_approved_minutes ?? 0)}/{mins(r.overtime_minutes)}
                    {level >= 1 && r.attendance_id && !r.locked && (r.overtime_approved_minutes ?? 0) < r.overtime_minutes && (
                      <button type="button" className="btn-ghost !px-2 !py-0.5 text-[10px]" disabled={busy} onClick={() => void act(() => hrApi.post(`/attendance/${r.attendance_id}/approve-overtime`, { minutes: r.overtime_minutes }), "hr.attendance.otApproved")}>{t("hr.approve")}</button>
                    )}
                  </span>
                ) : "—"}
              </td>
              <td className="text-end"><button type="button" className="btn-ghost !px-2 !py-0.5 text-[10px]" onClick={() => setDrill(r)}>{t("hr.attendance.punches")} ({r.punch_count ?? 0})</button></td>
            </tr>
          ))}
        </Table>
      </section>
      {(corrections.data ?? []).length > 0 && (
        <section className="card space-y-2 p-5">
          <SectionHeader icon="✏️" title={t("hr.attendance.pendingCorrections")} />
          <ul className="space-y-1 text-xs">
            {corrections.data!.map((c) => <li key={c.id} className="flex flex-wrap items-center gap-2 rounded-xl bg-gray-50 px-3 py-2"><b>{name(c)}</b><span className="num">{dateOnly(c.work_date, locale)}</span><span className="num">{t_(c.requested_in)} → {t_(c.requested_out)}</span><span className="text-gray-500">{c.reason}</span></li>)}
          </ul>
          <p className="text-[10px] font-semibold text-gray-400">{t("hr.attendance.decideInInbox")}</p>
        </section>
      )}
      <PunchesDialog row={drill} date={date} onClose={() => setDrill(null)} />
      <CorrectionDialog open={corr} onClose={() => setCorr(false)} forOthers={level >= 1} />
    </div>
  );
}
